import { Stack } from '@mui/material';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AutocompleteArrayInput, SelectInput, TextInput } from 'react-admin';
import {
  DateFromFilter,
  DateToFilter,
  PeriodFilter,
} from '../../../../Components/ReportsWidget/Filters';
import ReportWidget from '../../../../Components/ReportsWidget/Widget';
import type { ActionLogsByUsersResource } from '../../../../Entities/ActionLogsByUser';
import ActionLogsByUserDataTable from './ActionLogsByUserDataTable';
import PreviewButton from './PreviewButton';
import {
  ACTION_LOG_REPORT_ACTION_CHOICES,
  ACTION_LOG_REPORT_ENTITY_CHOICES,
  getActionLogReportAttributeChoices,
} from './utils';

function EntityTypesFilter(): ReactElement {
  return (
    <AutocompleteArrayInput
      source="entity_types"
      label="Entities"
      choices={[...ACTION_LOG_REPORT_ENTITY_CHOICES]}
    />
  );
}

function ActionFilter(): ReactElement {
  return (
    <SelectInput source="action" label="Action" choices={[...ACTION_LOG_REPORT_ACTION_CHOICES]} />
  );
}

function AttributeNamesFilter(): ReactElement {
  const { setValue } = useFormContext();
  const entityTypes = useWatch({ name: 'entity_types' }) as string[] | undefined;
  const attributeNames = useWatch({ name: 'attribute_names' }) as string[] | undefined;
  const action = useWatch({ name: 'action' }) as string | undefined;
  const choices = useMemo(() => getActionLogReportAttributeChoices(entityTypes), [entityTypes]);

  useEffect(() => {
    const currentAttributeNames = attributeNames ?? [];

    if (action !== 'update') {
      if (currentAttributeNames.length > 0) {
        setValue('attribute_names', []);
      }
      return;
    }

    const allowedAttributeIds = new Set(choices.map((choice) => choice.id));
    const nextAttributeNames = currentAttributeNames.filter((value) =>
      allowedAttributeIds.has(value),
    );

    const hasChanged =
      nextAttributeNames.length !== currentAttributeNames.length ||
      nextAttributeNames.some((value, index) => value !== currentAttributeNames[index]);

    if (hasChanged) {
      setValue('attribute_names', nextAttributeNames);
    }
  }, [action, attributeNames, choices, setValue]);

  return (
    <AutocompleteArrayInput
      source="attribute_names"
      label="Changed Attributes"
      choices={choices}
      disabled={action !== 'update' || choices.length === 0}
    />
  );
}

function UserIdentifierFilter(): ReactElement {
  return <TextInput source="user_identifier" label="User" />;
}

const filters = [
  PeriodFilter,
  DateFromFilter,
  DateToFilter,
  EntityTypesFilter,
  ActionFilter,
  AttributeNamesFilter,
  UserIdentifierFilter,
];

export default function ActionLogsByUserWidget(): ReactElement {
  const [hasPreview, setHasPreview] = useState(false);
  const [records, setRecords] = useState<ActionLogsByUsersResource[]>([]);
  const [isPending, setIsPending] = useState(false);

  return (
    <Stack spacing={1}>
      <ReportWidget
        resource="action_logs_by_users"
        filters={filters}
        defaultValues={{
          period: 'last_30_days',
        }}
        actions={
          <PreviewButton
            autoPreviewOnMount
            onPreviewStart={() => {
              setRecords([]);
              setHasPreview(true);
              setIsPending(true);
            }}
            onPreviewSuccess={(nextRecords) => {
              setRecords(nextRecords);
              setIsPending(false);
            }}
            onPreviewError={() => {
              setIsPending(false);
            }}
          />
        }
      />
      {hasPreview && <ActionLogsByUserDataTable data={records} isPending={isPending} />}
    </Stack>
  );
}
